import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import DateInputWithPicker from './DateInputWithPicker';
import './EditAvailabilityContextModal.css';

/**
 * EditAvailabilityContextModal - Edit blocked date context (dates + reason)
 *
 * ARCHITECTURAL CHANGE (2025):
 * - Now edits availability_contexts (range-based, fully editable)
 * - Replaces grouped overrides editing pattern
 * - Dates AND reason are fully editable
 *
 * Form modal pattern with purple header/footer
 * Allows editing both date range and reason for blocked availability contexts
 */
const EditAvailabilityContextModal = ({ isOpen, onClose, context, onSave }) => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reason, setReason] = useState('');
    const [blockType, setBlockType] = useState('holiday');
    const [validationError, setValidationError] = useState('');

    useEffect(() => {
        if (isOpen && context) {
            setStartDate(context.startDate ? DateTime.fromISO(context.startDate).toISODate() : '');
            setEndDate(context.endDate ? DateTime.fromISO(context.endDate).toISODate() : '');
            setReason(context.reason || '');
            setBlockType(context.context || 'holiday');
            setValidationError('');
        }
    }, [isOpen, context]);

    const handleSave = () => {
        // Validate dates
        if (!startDate || !endDate) {
            setValidationError('Please select both start and end dates');
            return;
        }

        const start = DateTime.fromISO(startDate);
        const end = DateTime.fromISO(endDate);

        if (end < start) {
            setValidationError('End date must be on or after start date');
            return;
        }

        if (!reason.trim()) {
            setValidationError('Please provide a reason');
            return;
        }

        const updates = {
            startDate: start.toISODate(),
            endDate: end.toISODate(),
            reason: reason.trim()
        };
        if (blockType !== context.context) {
            updates.context = blockType;
        }

        onSave(context.id, updates);

        onClose();
    };

    const handleCancel = () => {
        if (context) {
            setStartDate(context.startDate ? DateTime.fromISO(context.startDate).toISODate() : '');
            setEndDate(context.endDate ? DateTime.fromISO(context.endDate).toISODate() : '');
            setReason(context.reason || '');
            setBlockType(context.context || 'holiday');
        }
        setValidationError('');
        onClose();
    };

    if (!isOpen || !context) return null;

    return (
        <div className="edit-blocked-date-modal-overlay" onClick={handleCancel}>
            <div className="edit-blocked-date-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="edit-blocked-date-header">
                    <h2>Edit Availability Dates</h2>
                    <button
                        className="edit-blocked-date-close"
                        onClick={handleCancel}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                {/* Body */}
                <div className="edit-blocked-date-body">
                    {/* Validation Error */}
                    {validationError && (
                        <div className="alert alert-error" style={{ marginBottom: '16px' }}>
                            {validationError}
                        </div>
                    )}

                    {/* Date Range Row (Side by Side) */}
                    <div className="date-range-row">
                        {/* Start Date */}
                        <div className="form-group">
                            <label htmlFor="startDate" className="form-label">Start Date</label>
                            <DateInputWithPicker
                                id="startDate"
                                value={startDate}
                                onChange={(isoDate) => {
                                    setStartDate(isoDate);
                                    setValidationError('');
                                }}
                            />
                        </div>

                        {/* End Date */}
                        <div className="form-group">
                            <label htmlFor="endDate" className="form-label">End Date</label>
                            <DateInputWithPicker
                                id="endDate"
                                value={endDate}
                                onChange={(isoDate) => {
                                    setEndDate(isoDate);
                                    setValidationError('');
                                }}
                            />
                        </div>
                    </div>

                    {/* Type Picker */}
                    <div className="form-group">
                        <label htmlFor="editBlockType" className="form-label">Type</label>
                        <select
                            id="editBlockType"
                            className="form-input"
                            value={blockType}
                            onChange={(e) => setBlockType(e.target.value)}
                        >
                            <option value="holiday">Public Holiday</option>
                            <option value="personal">Personal Day</option>
                            <option value="leave">Annual Leave</option>
                        </select>
                    </div>

                    {/* Reason (Full Width) */}
                    <div className="form-group">
                        <label htmlFor="reason" className="form-label">Reason</label>
                        <input
                            type="text"
                            id="reason"
                            className="form-input"
                            placeholder="e.g., Christmas Holiday, Vacation, Personal"
                            value={reason}
                            onChange={(e) => {
                                setReason(e.target.value);
                                setValidationError('');
                            }}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="edit-blocked-date-footer">
                    <button
                        className="btn-secondary"
                        onClick={handleCancel}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleSave}
                        disabled={!startDate || !endDate || !reason.trim()}
                    >
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditAvailabilityContextModal;
